'use client'

import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface ManagedObject {
  external_object_id: string
  external_object_name: string | null
  attached_via: string
  attached_at: string
}

export function PushOptInDialog({
  open,
  onOpenChange,
  connectionId,
  platformName,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  connectionId: string
  platformName: string
  onSuccess: () => void | Promise<void>
}) {
  const [managedObjects, setManagedObjects] = useState<ManagedObject[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    fetch(`/api/connectors/connections/${connectionId}/push`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || 'Failed to load push status')
        return data as { managed_objects?: ManagedObject[] }
      })
      .then((data) => {
        if (cancelled) return
        const objects = data.managed_objects ?? []
        setManagedObjects(objects)
        setSelectedIds(new Set(objects.map((item) => item.external_object_id)))
      })
      .catch((err) => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load push status')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [connectionId, open])

  const detach = async (object: ManagedObject) => {
    const previous = new Set(selectedIds)
    const next = new Set(selectedIds)
    next.delete(object.external_object_id)
    setSelectedIds(next)

    const res = await fetch(`/api/connectors/connections/${connectionId}/push`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'detach', meta_campaign_id: object.external_object_id }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string }
      setSelectedIds(previous)
      toast.error(data.error || 'Failed to detach campaign')
    }
  }

  const enable = async () => {
    if (selectedIds.size === 0 || saving) return
    setSaving(true)
    try {
      const res = await fetch(`/api/connectors/connections/${connectionId}/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'enable' }),
      })
      const data = await res.json().catch(() => ({})) as { error?: string }
      if (!res.ok || data.error) {
        toast.error(data.error || 'Failed to enable push')
        return
      }
      await onSuccess()
    } finally {
      setSaving(false)
    }
  }

  const selectedCount = selectedIds.size

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Enable push for {platformName}?</DialogTitle>
          <DialogDescription>
            Atrium will set monthly budgets on the selected Meta campaigns via the Marketing API.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-sm text-atrium-muted">Loading campaigns...</div>
        ) : managedObjects.length === 0 ? (
          <div className="space-y-4 text-sm text-atrium-muted">
            <p>
              No campaigns attached for Atrium to manage. Connect your campaigns to Atrium
              first via chat: <code className="rounded bg-atrium-content px-1 py-0.5 text-atrium-black">meta_attach_campaign &lt;campaign_id&gt;</code>.
              We&apos;re working on a visual picker - coming soon.
            </p>
            <DialogFooter>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-lg border border-atrium-border bg-white px-4 py-2 text-sm text-atrium-black transition-colors hover:bg-atrium-content"
              >
                Dismiss
              </button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <p className="text-sm text-atrium-muted">
                Atrium will set monthly budgets on these {managedObjects.length} Meta campaigns
                via the Marketing API:
              </p>
              <div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-atrium-border bg-atrium-paper p-2">
                {managedObjects.map((object) => {
                  const checked = selectedIds.has(object.external_object_id)
                  return (
                    <label
                      key={object.external_object_id}
                      className="flex items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-atrium-content"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (!e.target.checked) void detach(object)
                        }}
                        disabled={!checked}
                        className="size-4 rounded border-atrium-border"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-atrium-black">
                          {object.external_object_name || object.external_object_id}
                        </span>
                        <span className="block truncate text-xs text-atrium-muted">
                          {object.external_object_id}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
              <p className="text-sm text-atrium-muted">
                We won&apos;t touch any other campaigns. You can revoke this anytime.
              </p>
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-lg border border-atrium-border bg-white px-4 py-2 text-sm text-atrium-black transition-colors hover:bg-atrium-content"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={enable}
                disabled={selectedCount === 0 || saving}
                className="rounded-lg bg-atrium-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-atrium-black/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Enabling...' : `Enable push for ${selectedCount} campaign${selectedCount === 1 ? '' : 's'}`}
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
